import { GroupDisplayFilter, StylesGroup } from '../types';
import { getAllowedPropertiesForAction } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/constants';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';

import {
  hasAnyMeaningfulValueCheckContext,
  isAllowedStylePresent,
} from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/actionValidators/actionVisibilityRules';
import { Meaningfulness } from '../../../apis/services/editActionsServices/nativeActions/globalDesign/common/meaningfulnessPredicates';
import { buildRootContextInfosMap } from '../common/grouping/helpers/buildRootContextInfosMap';
import { VirtualManifestAPI } from '@wix/editor-platform-manifest-services';

const cornerProperties = new Set(
  getAllowedPropertiesForAction(StylesDerivedNativeActions.cornerStyles),
);

const checkGroupValues = (group: StylesGroup) => {
  return hasAnyMeaningfulValueCheckContext(
    group.context,
    cornerProperties,
    Meaningfulness.meaningCheck(StylesDerivedNativeActions.cornerStyles),
    group.styleNamespace,
    new Set(group.items.map((item) => item.styleItemId)),
  );
};

/**
 * Corner group display filter
 *
 * Filters component groups in the corner panel based on corner radius values.
 *
 * Logic for each root component and its children:
 * 1. If ANY group (root or child) has a meaningful corner value:
 *    - Show only groups that have meaningful corner values (filters both roots and children)
 * 2. If NO group has meaningful corner values:
 *    - Show only root groups that are valid for corner properties (have manifest support)
 *    - Hide child groups
 *
 * Note: 0px is a meaningful value for corners (explicit sharp corner)
 */
export const cornerGroupDisplayFilter: GroupDisplayFilter = (
  groups: StylesGroup[],
  rootContexts: VirtualManifestAPI[],
): StylesGroup[] => {
  if (groups.length === 0) {
    return groups;
  }

  const result: StylesGroup[] = [];
  const rootContextInfos = buildRootContextInfosMap(groups, rootContexts);

  for (const rootContextInfo of Object.values(rootContextInfos)) {
    const contextGroups = [
      ...rootContextInfo.rootGroups,
      ...rootContextInfo.childGroups,
    ];

    const isSomeCornerRadiusVisible = contextGroups.some(checkGroupValues);

    if (isSomeCornerRadiusVisible) {
      result.push(...contextGroups.filter(checkGroupValues));
      continue;
    }

    for (const rootGroup of rootContextInfo.rootGroups) {
      if (
        isAllowedStylePresent(
          rootGroup.context,
          cornerProperties,
          rootGroup.styleNamespace,
        )
      ) {
        result.push(rootGroup);
      }
    }
  }

  return result;
};
